/**
 * @fileoverview Modal multi-etapas para criar um novo template Meta.
 *
 * Etapas:
 *   1. Fundamentos  — nome, categoria, idioma
 *   2. Conteudo     — header (opcional), body (obrigatorio), footer (opcional)
 *   3. Botoes       — ate 3 botoes (opcional)
 *   4. Revisao      — preview final + submit
 *
 * Filosofia: poucas escolhas por tela, preview sempre visivel, hints curtos.
 */

import React, { useMemo, useState, useEffect } from "react";
import { Dialog } from "@material-ui/core";
import { toast } from "react-toastify";
import {
  X,
  Megaphone,
  Info,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  Check,
  Plus,
  ExternalLink,
  Phone,
  CornerDownLeft,
  Image as ImageIcon,
  Video as VideoIcon,
  FileText,
  AlignLeft,
  Ban,
  Trash2,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useModalStyles } from "./modalStyles";
import {
  CATEGORY_LABELS,
  LANGUAGE_LABELS,
  LANGUAGE_OPTIONS,
} from "./statusConfig";
import {
  buildComponentsPayload,
  extractVars,
  parseComponentsToFlat,
  sanitizeTemplateName,
  validateBody,
  validateName,
} from "./componentsHelpers";
import TemplatePreview from "./TemplatePreview";
import MediaHeaderUploader from "./MediaHeaderUploader";

const STEPS = [
  { label: "Fundamentos" },
  { label: "Conteúdo" },
  { label: "Botões" },
  { label: "Revisão" },
];

const CATEGORIES = [
  {
    key: "MARKETING",
    label: CATEGORY_LABELS.MARKETING,
    description: "Promoções, novidades e ofertas.",
    Icon: Megaphone,
  },
  {
    key: "UTILITY",
    label: CATEGORY_LABELS.UTILITY,
    description: "Confirmações, avisos e atualizações.",
    Icon: Info,
  },
  {
    key: "AUTHENTICATION",
    label: CATEGORY_LABELS.AUTHENTICATION,
    description: "Códigos únicos e verificação.",
    Icon: ShieldCheck,
  },
];

const HEADER_OPTIONS = [
  { key: "NONE", label: "Sem cabeçalho", Icon: Ban, desc: "Ir direto para o corpo." },
  { key: "TEXT", label: "Texto", Icon: AlignLeft, desc: "Título curto (até 60)." },
  { key: "IMAGE", label: "Imagem", Icon: ImageIcon, desc: "Foto exemplo." },
  { key: "VIDEO", label: "Vídeo", Icon: VideoIcon, desc: "Videoclipe curto." },
  { key: "DOCUMENT", label: "Documento", Icon: FileText, desc: "PDF ou arquivo." },
];

const BUTTON_TYPES = [
  { key: "QUICK_REPLY", label: "Resposta rápida", Icon: CornerDownLeft },
  { key: "URL", label: "Abrir link", Icon: ExternalLink },
  { key: "PHONE_NUMBER", label: "Ligar", Icon: Phone },
];

const initialState = () => ({
  name: "",
  category: "MARKETING",
  language: "pt_BR",
  header: { type: "NONE", text: "", example: "", handle: "" },
  body: { text: "", examples: [] },
  footer: { text: "" },
  buttons: [],
});

const TemplateBuilderModal = ({
  open,
  onClose,
  onSuccess,
  connectionId,
  duplicateFrom,
}) => {
  const classes = useModalStyles();
  const [step, setStep] = useState(0);
  const [state, setState] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);

  useEffect(() => {
    if (duplicateFrom) {
      const flat = parseComponentsToFlat(duplicateFrom.components || []);
      setState({
        name: sanitizeTemplateName(`${duplicateFrom.name}_copy`),
        category: duplicateFrom.category || "MARKETING",
        language: duplicateFrom.language || "pt_BR",
        ...flat,
      });
    } else {
      setState(initialState());
    }
    setStep(0);
    setNameTouched(false);
  }, [duplicateFrom, open]);

  const bodyVars = useMemo(() => extractVars(state.body.text), [state.body.text]);

  const previewComponents = useMemo(
    () => buildComponentsPayload(state),
    [state]
  );

  const nameError = nameTouched ? validateName(state.name) : null;
  const bodyError = step > 0 ? validateBody(state.body.text) : null;

  const canGoNext = (() => {
    if (step === 0) {
      return !validateName(state.name) && state.category && state.language;
    }
    if (step === 1) {
      if (validateBody(state.body.text)) return false;
      if (state.header.type === "TEXT" && !state.header.text) return false;
      // Header de midia exige upload concluido (handle da Meta)
      if (
        ["IMAGE", "VIDEO", "DOCUMENT"].includes(state.header.type) &&
        !state.header.handle
      ) {
        return false;
      }
      return true;
    }
    if (step === 2) {
      const invalid = state.buttons.some((b) => {
        if (!b.text) return true;
        if (b.type === "URL" && !b.url) return true;
        if (b.type === "PHONE_NUMBER" && !b.phone_number) return true;
        return false;
      });
      return !invalid;
    }
    return true;
  })();

  const update = (patch) => setState((s) => ({ ...s, ...patch }));
  const updateHeader = (patch) =>
    setState((s) => ({ ...s, header: { ...s.header, ...patch } }));
  const updateBody = (patch) =>
    setState((s) => ({ ...s, body: { ...s.body, ...patch } }));
  const updateFooter = (patch) =>
    setState((s) => ({ ...s, footer: { ...s.footer, ...patch } }));

  const addButton = (type) => {
    if (state.buttons.length >= 3) return;
    setState((s) => ({
      ...s,
      buttons: [...s.buttons, { type, text: "", url: "", phone_number: "" }],
    }));
  };

  const updateButton = (idx, patch) => {
    setState((s) => {
      const copy = s.buttons.slice();
      copy[idx] = { ...copy[idx], ...patch };
      return { ...s, buttons: copy };
    });
  };

  const removeButton = (idx) => {
    setState((s) => ({
      ...s,
      buttons: s.buttons.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async () => {
    if (!connectionId) return;
    setSubmitting(true);
    try {
      const payload = {
        name: state.name,
        language: state.language,
        category: state.category,
        components: buildComponentsPayload(state),
      };
      await api.post(`/meta/templates/${connectionId}`, payload);
      toast.success("Template enviado para aprovação da Meta.");
      if (onSuccess) onSuccess();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else handleSubmit();
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <div className={classes.dialogInner}>
        <div className={classes.formCol}>
          <div className={classes.header}>
            <div className={classes.eyebrow}>
              {duplicateFrom ? "Duplicar template" : "Novo template"}
            </div>
            <div className={classes.title}>
              {STEPS[step].label}
            </div>
            <div className={classes.subtitle}>
              {step === 0 &&
                "Comece dando um nome claro e escolhendo a categoria correta."}
              {step === 1 &&
                "O corpo é obrigatório. Cabeçalho e rodapé são opcionais."}
              {step === 2 &&
                "Botões são opcionais. Combine até 3 ações rápidas, links e chamadas."}
              {step === 3 &&
                "Confirme os detalhes. Após enviar, a Meta analisa em até alguns minutos."}
            </div>
            <button
              type="button"
              className={classes.closeBtn}
              onClick={onClose}
              disabled={submitting}
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>

          <div className={classes.stepper}>
            {STEPS.map((s, i) => (
              <React.Fragment key={s.label}>
                <div className={classes.stepItem}>
                  <div
                    className={`${classes.stepNumber} ${
                      i < step
                        ? classes.stepNumberDone
                        : i === step
                        ? classes.stepNumberActive
                        : classes.stepNumberIdle
                    }`}
                  >
                    {i < step ? <Check size={12} strokeWidth={2.5} /> : i + 1}
                  </div>
                  <div
                    className={`${classes.stepLabel} ${
                      i === step ? classes.stepLabelActive : ""
                    }`}
                  >
                    {s.label}
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={classes.stepConnector} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className={classes.body}>
            {step === 0 && (
              <>
                <div className={classes.fieldGroup}>
                  <div className={classes.label}>
                    Nome do template
                    <span className={classes.labelHint}>
                      minúsculas, números e _
                    </span>
                  </div>
                  <input
                    className={classes.input}
                    placeholder="ex: confirmacao_pedido"
                    value={state.name}
                    onChange={(e) => {
                      setNameTouched(true);
                      update({ name: sanitizeTemplateName(e.target.value) });
                    }}
                    onBlur={() => setNameTouched(true)}
                    maxLength={60}
                  />
                  {nameError ? (
                    <div className={classes.error}>{nameError}</div>
                  ) : (
                    <div className={classes.hint}>
                      Usado internamente pela Meta. Não pode ser alterado depois.
                    </div>
                  )}
                </div>

                <div className={classes.fieldGroup}>
                  <div className={classes.label}>Categoria</div>
                  <div className={classes.cardGrid}>
                    {CATEGORIES.map(({ key, label, description, Icon }) => (
                      <div
                        key={key}
                        className={`${classes.choiceCard} ${
                          state.category === key ? classes.choiceCardActive : ""
                        }`}
                        onClick={() => update({ category: key })}
                      >
                        <Icon
                          size={18}
                          strokeWidth={1.5}
                          className={classes.choiceIcon}
                        />
                        <div className={classes.choiceTitle}>{label}</div>
                        <div className={classes.choiceDesc}>{description}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={classes.fieldGroup}>
                  <div className={classes.label}>Idioma</div>
                  <select
                    className={classes.select}
                    value={state.language}
                    onChange={(e) => update({ language: e.target.value })}
                  >
                    {LANGUAGE_OPTIONS.map((l) => (
                      <option key={l} value={l}>
                        {LANGUAGE_LABELS[l]}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div className={classes.fieldGroup}>
                  <div className={classes.label}>Cabeçalho (opcional)</div>
                  <div className={classes.cardGrid}>
                    {HEADER_OPTIONS.map(({ key, label, desc, Icon }) => (
                      <div
                        key={key}
                        className={`${classes.choiceCard} ${
                          state.header.type === key
                            ? classes.choiceCardActive
                            : ""
                        }`}
                        onClick={() =>
                          updateHeader({
                            type: key,
                            // ao trocar tipo, zera campos especificos do tipo anterior
                            text: key === "TEXT" ? state.header.text : "",
                            handle: "",
                            fileName: "",
                            mimeType: "",
                            size: 0,
                            previewUrl: null,
                          })
                        }
                        style={{ minHeight: 0 }}
                      >
                        <Icon
                          size={16}
                          strokeWidth={1.5}
                          className={classes.choiceIcon}
                        />
                        <div className={classes.choiceTitle}>{label}</div>
                        <div className={classes.choiceDesc}>{desc}</div>
                      </div>
                    ))}
                  </div>
                  {state.header.type === "TEXT" && (
                    <>
                      <input
                        className={classes.input}
                        placeholder="Título do cabeçalho"
                        value={state.header.text}
                        onChange={(e) =>
                          updateHeader({ text: e.target.value.slice(0, 60) })
                        }
                        maxLength={60}
                        style={{ marginTop: 8 }}
                      />
                      <div className={classes.counter}>
                        {state.header.text.length}/60
                      </div>
                    </>
                  )}
                  {["IMAGE", "VIDEO", "DOCUMENT"].includes(
                    state.header.type
                  ) && (
                    <MediaHeaderUploader
                      format={state.header.type}
                      header={state.header}
                      connectionId={connectionId}
                      onChange={(patch) => updateHeader(patch)}
                    />
                  )}
                </div>

                <div className={classes.sectionDivider} />

                <div className={classes.fieldGroup}>
                  <div className={classes.label}>
                    Corpo da mensagem
                    <span className={classes.labelHint}>obrigatório</span>
                  </div>
                  <textarea
                    className={`${classes.input} ${classes.textarea}`}
                    placeholder="Olá {{1}}, seu pedido {{2}} foi confirmado. Use {{N}} para inserir variáveis."
                    value={state.body.text}
                    onChange={(e) =>
                      updateBody({ text: e.target.value.slice(0, 1024) })
                    }
                    maxLength={1024}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div className={classes.hint}>
                      {bodyVars.length > 0
                        ? `Variáveis detectadas: ${bodyVars
                            .map((v) => `{{${v}}}`)
                            .join(", ")}`
                        : "Use {{1}}, {{2}} para valores dinâmicos."}
                    </div>
                    <div className={classes.counter}>
                      {state.body.text.length}/1024
                    </div>
                  </div>
                  {bodyError && (
                    <div className={classes.error}>{bodyError}</div>
                  )}
                </div>

                {bodyVars.length > 0 && (
                  <div className={classes.fieldGroup}>
                    <div className={classes.label}>
                      Exemplos para cada variável
                      <span className={classes.labelHint}>
                        usado só na aprovação
                      </span>
                    </div>
                    {bodyVars.map((v, i) => (
                      <input
                        key={v}
                        className={classes.input}
                        placeholder={`Exemplo para {{${v}}}`}
                        value={state.body.examples[i] || ""}
                        onChange={(e) => {
                          const copy = state.body.examples.slice();
                          copy[i] = e.target.value;
                          updateBody({ examples: copy });
                        }}
                      />
                    ))}
                  </div>
                )}

                <div className={classes.sectionDivider} />

                <div className={classes.fieldGroup}>
                  <div className={classes.label}>Rodapé (opcional)</div>
                  <input
                    className={classes.input}
                    placeholder="Texto curto no rodapé"
                    value={state.footer.text}
                    onChange={(e) =>
                      updateFooter({ text: e.target.value.slice(0, 60) })
                    }
                    maxLength={60}
                  />
                  <div className={classes.counter}>
                    {state.footer.text.length}/60
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className={classes.hint}>
                  Adicione até 3 botões. Você pode misturar tipos, mas botões de
                  link e de ligar usam regras adicionais da Meta.
                </div>

                <div className={classes.buttonRow}>
                  {state.buttons.map((b, i) => (
                    <div key={i} className={classes.buttonCard}>
                      <div className={classes.buttonCardHeader}>
                        <div className={classes.buttonCardType}>
                          {BUTTON_TYPES.find((t) => t.key === b.type)?.label ||
                            b.type}
                        </div>
                        <button
                          type="button"
                          className={classes.removeBtn}
                          onClick={() => removeButton(i)}
                        >
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                      <input
                        className={classes.input}
                        placeholder="Texto visível no botão"
                        value={b.text}
                        onChange={(e) =>
                          updateButton(i, {
                            text: e.target.value.slice(0, 25),
                          })
                        }
                        maxLength={25}
                      />
                      {b.type === "URL" && (
                        <input
                          className={classes.input}
                          placeholder="https://exemplo.com/{{1}}"
                          value={b.url}
                          onChange={(e) =>
                            updateButton(i, { url: e.target.value })
                          }
                        />
                      )}
                      {b.type === "PHONE_NUMBER" && (
                        <input
                          className={classes.input}
                          placeholder="+55 11 90000-0000"
                          value={b.phone_number}
                          onChange={(e) =>
                            updateButton(i, { phone_number: e.target.value })
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>

                {state.buttons.length < 3 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {BUTTON_TYPES.map(({ key, label, Icon }) => (
                      <button
                        key={key}
                        type="button"
                        className={classes.toggleBtn}
                        onClick={() => addButton(key)}
                      >
                        <Icon size={14} strokeWidth={1.5} />
                        <Plus size={12} strokeWidth={2} />
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {step === 3 && (
              <>
                <div className={classes.reviewSection}>
                  <div className={classes.reviewLabel}>Nome</div>
                  <div
                    className={classes.reviewValue}
                    style={{
                      fontFamily:
                        "ui-monospace, 'SF Mono', 'Menlo', monospace",
                    }}
                  >
                    {state.name}
                  </div>
                </div>
                <div className={classes.reviewSection}>
                  <div className={classes.reviewLabel}>Categoria</div>
                  <div className={classes.reviewValue}>
                    {CATEGORY_LABELS[state.category]}
                  </div>
                </div>
                <div className={classes.reviewSection}>
                  <div className={classes.reviewLabel}>Idioma</div>
                  <div className={classes.reviewValue}>
                    {LANGUAGE_LABELS[state.language] || state.language}
                  </div>
                </div>
                {bodyVars.length > 0 && (
                  <div className={classes.reviewSection}>
                    <div className={classes.reviewLabel}>
                      Variáveis detectadas
                    </div>
                    <div className={classes.reviewValue}>
                      {bodyVars.map((v) => `{{${v}}}`).join(" · ")}
                    </div>
                  </div>
                )}
                <div className={classes.hint}>
                  Após enviar, a Meta analisa o conteúdo. Templates de
                  autenticação são avaliados em minutos; marketing pode levar
                  até 24h.
                </div>
              </>
            )}
          </div>

          <div className={classes.footer}>
            <button
              type="button"
              className={classes.btnGhost}
              onClick={step === 0 ? onClose : handleBack}
              disabled={submitting}
            >
              {step === 0 ? (
                "Cancelar"
              ) : (
                <>
                  <ChevronLeft size={15} strokeWidth={1.5} /> Voltar
                </>
              )}
            </button>
            <button
              type="button"
              className={classes.btnPrimary}
              onClick={handleNext}
              disabled={!canGoNext || submitting}
            >
              {step === STEPS.length - 1 ? (
                submitting ? (
                  "Enviando..."
                ) : (
                  <>
                    <Check size={15} strokeWidth={2} /> Enviar para aprovação
                  </>
                )
              ) : (
                <>
                  Continuar <ChevronRight size={15} strokeWidth={1.5} />
                </>
              )}
            </button>
          </div>
        </div>

        <div className={classes.previewCol}>
          <div className={classes.previewTitle}>Pré-visualização</div>
          <TemplatePreview
            components={previewComponents}
            emptyHint="O preview aparece aqui conforme você preenche o conteúdo."
            headerMediaUrl={state.header?.previewUrl || null}
          />
          <div className={classes.previewMeta}>
            {bodyVars.length > 0 ? (
              <>
                As variáveis destacadas serão substituídas pelos valores reais
                no envio.
              </>
            ) : (
              "Use {{1}}, {{2}} no corpo para personalizar com dados do contato."
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TemplateBuilderModal;
